import { type CSSProperties, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DownChevronIcon, YellowWarningIcon } from '@repo/atomic-components';
import { useGetChatDocumentGetPreviewQuery } from '@/api/chat/chatDocumentGetPreview';
import { selectChatDocumentPanelSettings, useAppSelector } from '@/store';
import { parseImageLabelling } from '@/utils/imageLabelling';
import { ImagePreviewToolbar } from './ImagePreviewToolbar';
import { imageDetailsContainerId, imageOcrContainerId } from './imageContainerIds';
import { documentMarkdownComponents } from './markdownComponents';
import { useImagePreviewRotation } from './useImagePreviewRotation';
import { useImagePreviewZoom } from './useImagePreviewZoom';
import { containsSearchText, usePdfTextSearch } from './usePdfTextSearch';

/** AI warning for image details. */
export const AI_IMAGE_DETAILS_WARNING =
  'These details were written by AI from the image, please check the image carefully for accuracy';

type ImageOcrSectionProps = {
  ocrText: string;
  /** Id the citation highlighter searches within, and the button's `aria-controls` target. */
  containerId: string;
  /** Open on mount because a citation sent the reader straight to this transcription. */
  defaultOpen: boolean;
  /** Cited extract to highlight inside the transcription, if it came from the OCR text. */
  highlightText?: string;
  highlightTrigger?: number;
};

/** OCR transcription behind a button. It has no coordinates, so it can't be drawn over the image. */
const ImageOcrSection = ({
  ocrText,
  containerId,
  defaultOpen,
  highlightText,
  highlightTrigger,
}: ImageOcrSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // Only a highlight the panel is actually showing can be found in the DOM; the hook keeps
  // retrying, so opening the panel later still lands the mark.
  usePdfTextSearch(isOpen ? highlightText : undefined, undefined, highlightTrigger, containerId);

  return (
    <div className="border-theme-border-grey bg-theme-white rounded-md border">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={containerId}
        onClick={() => setIsOpen((open) => !open)}
        className="text-theme-black flex w-full cursor-pointer items-center justify-between gap-2 border-none bg-transparent px-4 py-3 text-left text-sm font-semibold"
      >
        <span>Text in this image</span>
        <DownChevronIcon
          className={`h-[16px] w-[16px] shrink-0 transition-transform ${isOpen ? 'rotate-180' : 'rotate-0'}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          id={containerId}
          className="border-theme-border-grey text-theme-black max-h-[50vh] overflow-y-auto border-t px-4 py-3 text-sm"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={documentMarkdownComponents}>
            {ocrText}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

const ROTATION_CLASS: Record<number, string> = { 90: 'rotate-90', 180: 'rotate-180', 270: 'rotate-270' };

type LoadedImageSize = { key: string; width: number; height: number };

/**
 * Sizes for an image turned `rotation` degrees clockwise and scaled by `zoom`. It keeps its upright
 * size unless a quarter turn would take it past the panel's width (or the 60vh cap above the
 * details), and the box takes the turned footprint so centring and scrolling follow what's on screen.
 */
function getImageLayout(
  { width, height }: Omit<LoadedImageSize, 'key'>,
  rotation: number,
  capHeight: boolean,
  zoom: number,
): { box: CSSProperties; image: CSSProperties } {
  const ratio = width / height;
  const isQuarterTurn = rotation % 180 !== 0;
  // What `max-w-full` and `max-h-[60vh]` give the upright image. `cqw` is the panel's content width.
  type Limit = [value: number, unit: string];
  const widthLimits: Limit[] = [
    [width, 'px'],
    [100, 'cqw'],
  ];
  if (capHeight) widthLimits.push([60 * ratio, 'vh']);
  // Turned sideways, the image's height runs across the panel and its width up it.
  if (isQuarterTurn) widthLimits.push([100 * ratio, 'cqw'], ...(capHeight ? [[60, 'vh'] as Limit] : []));

  // Zoom scales every limit, so the fitted size grows by the same factor.
  const imageWidth = `min(${widthLimits.map(([value, unit]) => `${value * zoom}${unit}`).join(', ')})`;
  const imageHeight = `calc(${imageWidth} / ${ratio})`;
  return {
    box: isQuarterTurn ? { width: imageHeight, height: imageWidth } : { width: imageWidth, height: imageHeight },
    image: { width: imageWidth, height: imageHeight },
  };
}

export type ImageDocumentViewerProps = {
  fileUrl: string;
  documentId: string;
  documentName?: string;
  highlightText?: string;
  highlightTrigger?: number;
};

/** Image viewer with the AI description and OCR text, when present. */
export function ImageDocumentViewer({
  fileUrl,
  documentId,
  documentName,
  highlightText: highlightTextProp,
  highlightTrigger: highlightTriggerProp,
}: ImageDocumentViewerProps) {
  const imagePreviewKey = `${documentId}\0${fileUrl}`;
  const [failedImagePreviewKey, setFailedImagePreviewKey] = useState<string | null>(null);
  const imageErrored = failedImagePreviewKey === imagePreviewKey;
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [loadedImageSize, setLoadedImageSize] = useState<LoadedImageSize | null>(null);
  const { rotation, rotateClockwise, rotateAnticlockwise } = useImagePreviewRotation(documentId);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Not kept anywhere: reopening the preview mounts it afresh at 100%, like the PDF viewer.
  const { zoom, zoomIn, zoomOut, canZoomIn, canZoomOut, isPanning, panHandlers } = useImagePreviewZoom(
    scrollRef,
    imgRef,
  );

  // The side panel reads the citation from the store; the standalone viewer tab passes props.
  const allSettings = useAppSelector(selectChatDocumentPanelSettings);
  const panelSettings = allSettings[documentId];
  const highlightText = highlightTextProp ?? panelSettings?.highlightText;
  const highlightTrigger = highlightTriggerProp ?? panelSettings?.highlightTrigger;

  // Already in the RTK Query cache: it is the request that produced `fileUrl`.
  const { data: preview } = useGetChatDocumentGetPreviewQuery({ chatDocumentId: documentId }, { skip: !documentId });
  const labelling = parseImageLabelling(preview);
  const caption = labelling?.caption;
  const ocrText = labelling?.ocrText;

  // Whether the extract comes from the OCR text, found by matching text (citations don't say which).
  const citedFromOcr = !!(highlightText && ocrText && containsSearchText(ocrText, highlightText));
  const citedFromDetails = !!(highlightText && caption && containsSearchText(caption, highlightText));

  // The details are always on screen, so the mark only has to be drawn — the transcription
  // has to be opened first, which `ImageOcrSection` handles.
  usePdfTextSearch(
    citedFromDetails ? highlightText : undefined,
    undefined,
    highlightTrigger,
    imageDetailsContainerId(documentId),
  );

  const isImageLoaded = loadedImageSize?.key === imagePreviewKey;
  const imageSize = isImageLoaded && loadedImageSize.width > 0 && loadedImageSize.height > 0 ? loadedImageSize : null;
  const isTransformed = rotation !== 0 || zoom !== 1;
  const layout = isTransformed && imageSize ? getImageLayout(imageSize, rotation, !!labelling, zoom) : null;

  const image = imageErrored ? (
    <div className="text-theme-text-grey text-center text-sm">Image preview couldn’t be loaded.</div>
  ) : (
    // `contents` keeps the upright image laid out as before; turned or zoomed, the box takes its footprint.
    <div className={layout ? 'relative shrink-0 shadow-md' : 'contents'} style={layout?.box}>
      <img
        key={fileUrl}
        ref={imgRef}
        src={fileUrl}
        alt={documentName || 'Document image'}
        className={
          layout
            ? `absolute top-1/2 left-1/2 max-w-none -translate-x-1/2 -translate-y-1/2 select-none ${ROTATION_CLASS[rotation] ?? ''}`
            : // Hidden until it loads, so a turned preview doesn't flash upright first.
              `max-w-full object-contain shadow-md select-none ${labelling ? 'max-h-[60vh]' : 'max-h-full'} ${isTransformed && !isImageLoaded ? 'invisible' : ''}`
        }
        style={layout?.image}
        draggable={false}
        onLoad={(e) => {
          setFailedImagePreviewKey(null);
          const { naturalWidth, naturalHeight } = e.currentTarget;
          setLoadedImageSize({ key: imagePreviewKey, width: naturalWidth, height: naturalHeight });
        }}
        onError={(e) => {
          if (e.currentTarget !== imgRef.current) return;
          setFailedImagePreviewKey(imagePreviewKey);
        }}
      />
    </div>
  );

  // Dragging anywhere around the image moves a zoomed one about.
  const imageAreaProps = {
    ...panHandlers,
    className: zoom > 1 ? (isPanning ? 'cursor-grabbing' : 'cursor-grab') : '',
  };

  // Safe centring overflows to the right and bottom, where a zoomed image can be scrolled to.
  return (
    <div className="group relative h-full w-full">
      <div
        ref={scrollRef}
        className="border-theme-border-grey hide-scrollbar relative mx-0 h-full w-full overflow-auto border-l bg-transparent pt-14"
      >
        {!labelling ? (
          <div
            {...imageAreaProps}
            className={`@container flex min-h-full w-full [align-items:safe_center] [justify-content:safe_center] p-6 ${imageAreaProps.className}`}
          >
            {image}
          </div>
        ) : (
          <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-6">
            <div
              {...imageAreaProps}
              className={`@container flex [justify-content:safe_center] ${imageAreaProps.className}`}
            >
              {image}
            </div>

            {caption && (
              <section className="border-theme-border-grey bg-theme-white rounded-md border px-4 py-3">
                <h2 className="text-theme-black m-0 text-sm font-semibold">Details</h2>
                <p className="text-theme-text-grey m-0 mt-1 flex items-start gap-2 text-xs leading-4">
                  <YellowWarningIcon width={14} height={14} className="mt-[1px] shrink-0" aria-hidden />
                  <span>{AI_IMAGE_DETAILS_WARNING}</span>
                </p>
                <p
                  id={imageDetailsContainerId(documentId)}
                  className="text-theme-black mt-3 mb-0 text-sm leading-5 break-words whitespace-pre-wrap"
                >
                  {caption}
                </p>
              </section>
            )}

            {ocrText && (
              <ImageOcrSection
                // A new citation re-opens the transcription even if the reader had collapsed it.
                key={`${documentId}-${highlightTrigger ?? ''}`}
                ocrText={ocrText}
                containerId={imageOcrContainerId(documentId)}
                defaultOpen={citedFromOcr}
                highlightText={citedFromOcr ? highlightText : undefined}
                highlightTrigger={highlightTrigger}
              />
            )}
          </div>
        )}
      </div>
      {!imageErrored && (
        <ImagePreviewToolbar
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onRotateAnticlockwise={rotateAnticlockwise}
          onRotateClockwise={rotateClockwise}
          canZoomIn={canZoomIn}
          canZoomOut={canZoomOut}
        />
      )}
    </div>
  );
}
